/* femme Appstle product UI V11 | 2026-09-23
   Multi Choice Plan UI: preserves the signed-off femme purchase selector,
   submits the exact selected Appstle selling plan, and adds a smooth eased
   cadence-menu open/close transition without changing subscription logic.
   V9 marks the closest available cadence as Recommended when the product has
   exactly one valid Supply_XX tag. Recommendation is advisory only.
   V10 aligns active dropdown accents to femme mulberry with white iconography.
   V11 keeps Appstle's native widget DOM alive but visually suppressed, preventing
   Appstle and the femme MutationObserver from entering a remove/reinsert loop.
   Native selling-plan inputs are preserved; the formdata/fetch guards remain
   authoritative for the actual Shopify selling_plan submitted to cart.
*/
(() => {
  'use strict';

  const A = window.FemmeAppstleMigration;
  if (!A) return;

  const S = A.state;

  A.getVariantId = () => {
    const input =
      S.root?.querySelector('form[action*="/cart/add"] input[name="id"]') ||
      S.root?.querySelector('input[name="id"]');

    return input?.value
      ? String(input.value)
      : new URL(location.href).searchParams.get('variant') ||
          String(S.product?.variants?.[0]?.id || '');
  };

  A.getVariant = () => S.variants.get(A.getVariantId()) || null;
  A.getPlans = (variant = A.getVariant()) =>
    Array.isArray(variant?.plans) ? variant.plans : [];
  A.getPlan = (variant = A.getVariant()) => {
    const plans = A.getPlans(variant);
    return (
      plans.find((p) => p.id === String(S.selectedPlanId)) ||
      A.defaultPlan(plans) ||
      null
    );
  };

  A.supplyDays = () => {
    const rawTags = Array.isArray(S.product?.tags)
      ? S.product.tags
      : typeof S.product?.tags === 'string'
        ? S.product.tags.split(',')
        : [];

    const supplyTags = rawTags
      .map((tag) => String(tag || '').trim())
      .filter((tag) => /^supply_/i.test(tag));

    if (supplyTags.length !== 1) return null;

    const match = supplyTags[0].match(/^supply_(\d+(?:\.\d+)?)$/i);
    if (!match) return null;

    const days = Number(match[1]);
    return Number.isFinite(days) && days > 0 ? days : null;
  };

  A.planCadenceDays = (plan) => {
    const id = A.normaliseId(plan?.id);
    const known = {
      '713609642371': 14,
      '713609675139': 30,
      '713609707907': 60,
      '713609740675': 90
    };
    if (known[id]) return known[id];

    const label = A.planDisplayLabel(plan);
    if (/every\s+2\s+weeks/i.test(label)) return 14;

    const monthMatch = label.match(/every\s+(?:(\d+)\s+)?months?/i);
    if (monthMatch) return (Number(monthMatch[1] || 1) || 1) * 30;

    return null;
  };

  A.recommendedPlanId = (plans = A.getPlans()) => {
    const supplyDays = A.supplyDays();
    if (!supplyDays || !Array.isArray(plans) || !plans.length) return '';

    let bestId = '';
    let bestDistance = Infinity;
    let bestCadence = Infinity;

    plans.forEach((plan) => {
      const cadenceDays = A.planCadenceDays(plan);
      if (!cadenceDays) return;

      const distance = Math.abs(supplyDays - cadenceDays);
      if (
        distance < bestDistance ||
        (distance === bestDistance && cadenceDays < bestCadence)
      ) {
        bestId = String(plan.id || '');
        bestDistance = distance;
        bestCadence = cadenceDays;
      }
    });

    return bestId;
  };

  A.quantity = () => {
    const list = Array.from(
      S.root?.querySelectorAll('input[name="quantity"]') || []
    );
    const input =
      list.find((el) => !el.disabled && el.getClientRects().length) || list[0];
    const value = Number.parseInt(input?.value || '1', 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  };

  A.saving = (plan, variant) => {
    const compare = Number(plan?.compareAt || variant?.price || 0);
    const price = Number(plan?.price || 0);
    return compare > price && compare > 0
      ? Math.round(((compare - price) / compare) * 100)
      : 0;
  };

  A.token = (text, percent) =>
    String(text || '')
      .replace(/\[saving\]/gi, percent > 0 ? `${percent}%` : '')
      .replace(/\s{2,}/g, ' ')
      .trim();

  A.subLabel = (plan, variant) => {
    const percent = A.saving(plan, variant);
    let value = String(S.config?.subscriptionLabel || 'Subscribe & save').trim();
    if (
      S.config?.appendSavingLabel &&
      percent > 0 &&
      !value.includes('[saving]')
    ) {
      value += ` ${percent}%`;
    }
    return A.token(value, percent);
  };

  A.subCta = (plan, variant) => {
    const percent = A.saving(plan, variant);
    let value = String(S.config?.subscriptionCta || 'Subscribe & save').trim();
    if (
      S.config?.appendSavingCta &&
      percent > 0 &&
      !value.includes('[saving]')
    ) {
      value += ` ${percent}%`;
    }
    return A.token(value, percent);
  };

  const head = (type) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'fsc-option-head';
    button.dataset.fscOption = type;
    button.setAttribute('role', 'radio');
    button.setAttribute('aria-checked', 'false');

    const radio = document.createElement('span');
    radio.className = 'fsc-radio';
    radio.setAttribute('aria-hidden', 'true');

    const label = document.createElement('span');
    label.className = 'fsc-option-label';

    const price = document.createElement('span');
    price.className = 'fsc-option-price';

    button.append(radio, label, price);
    return { button, label, price };
  };

  A.installPlanPickerStyles = () => {
    if (document.getElementById('femme-appstle-plan-picker-styles')) return;

    const style = document.createElement('style');
    style.id = 'femme-appstle-plan-picker-styles';
    style.textContent = `
      .fsc-ui .fsc-plan-picker[hidden] {
        display: none !important;
      }

      .fsc-ui .fsc-plan-picker {
        width: 100%;
      }

      .fsc-ui .fsc-plan-trigger::before,
      .fsc-ui .fsc-plan-trigger::after,
      .fsc-ui .fsc-plan-option::before,
      .fsc-ui .fsc-plan-option::after {
        content: none !important;
        display: none !important;
        background: none !important;
        background-image: none !important;
      }

      .fsc-ui .fsc-plan-trigger {
        appearance: none;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        width: 100%;
        min-height: var(--fsc-dropdown-height, 44px);
        margin: 0;
        padding: 5px 7px 5px 15px;
        border: 0;
        border-radius: 10px;
        background: var(--fsc-dropdown-bg, #ffffff);
        box-shadow: inset 0 0 0 1px rgba(0, 15, 32, 0.14);
        color: var(--fsc-text, #000f20);
        font: inherit;
        font-size: var(--fsc-detail-font-size, 13px);
        font-weight: 500;
        line-height: 1.25;
        text-align: left;
        cursor: pointer;
        transition: box-shadow 160ms ease, transform 160ms ease;
      }

      .fsc-ui .fsc-plan-trigger:hover {
        box-shadow:
          inset 0 0 0 1px rgba(0, 15, 32, 0.22),
          0 5px 14px rgba(0, 15, 32, 0.06);
      }

      .fsc-ui .fsc-plan-trigger:focus-visible {
        outline: 2px solid #7c2946;
        outline-offset: 2px;
      }

      .fsc-ui .fsc-plan-picker.is-open .fsc-plan-trigger {
        box-shadow:
          inset 0 0 0 2px #7c2946,
          0 6px 18px rgba(0, 15, 32, 0.08);
      }

      .fsc-ui .fsc-plan-trigger-label {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .fsc-ui .fsc-plan-trigger-icon {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        flex: 0 0 30px;
        border-radius: 999px;
        background: #f7f4e1;
        color: #000f20;
        transition:
          transform 220ms cubic-bezier(0.4, 0, 0.2, 1),
          background-color 220ms cubic-bezier(0.4, 0, 0.2, 1);
      }

      .fsc-ui .fsc-plan-trigger-icon::before {
        content: '';
        width: 7px;
        height: 7px;
        margin-top: -3px;
        border-right: 1.5px solid currentColor;
        border-bottom: 1.5px solid currentColor;
        transform: rotate(45deg);
      }

      .fsc-ui .fsc-plan-picker.is-open .fsc-plan-trigger-icon {
        transform: rotate(180deg);
        background: #7c2946;
        color: #ffffff;
      }

      .fsc-ui .fsc-plan-menu {
        display: grid;
        gap: 3px;
        width: 100%;
        box-sizing: border-box;
        max-height: 0;
        margin-top: 0;
        padding: 0 6px;
        overflow: hidden;
        border-radius: 10px;
        background: #ffffff;
        box-shadow:
          0 12px 30px rgba(0, 15, 32, 0.12),
          inset 0 0 0 1px rgba(0, 15, 32, 0.08);
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
        transform: translateY(-6px);
        transform-origin: top;
        transition:
          max-height 260ms cubic-bezier(0.4, 0, 0.2, 1),
          margin-top 240ms cubic-bezier(0.4, 0, 0.2, 1),
          padding-top 240ms cubic-bezier(0.4, 0, 0.2, 1),
          padding-bottom 240ms cubic-bezier(0.4, 0, 0.2, 1),
          opacity 200ms cubic-bezier(0.4, 0, 0.2, 1),
          transform 240ms cubic-bezier(0.4, 0, 0.2, 1),
          visibility 0s linear 260ms;
      }

      .fsc-ui .fsc-plan-picker.is-open .fsc-plan-menu {
        max-height: 280px;
        margin-top: 7px;
        padding-top: 6px;
        padding-bottom: 6px;
        opacity: 1;
        visibility: visible;
        pointer-events: auto;
        transform: translateY(0);
        transition:
          max-height 260ms cubic-bezier(0.4, 0, 0.2, 1),
          margin-top 240ms cubic-bezier(0.4, 0, 0.2, 1),
          padding-top 240ms cubic-bezier(0.4, 0, 0.2, 1),
          padding-bottom 240ms cubic-bezier(0.4, 0, 0.2, 1),
          opacity 200ms cubic-bezier(0.4, 0, 0.2, 1),
          transform 240ms cubic-bezier(0.4, 0, 0.2, 1),
          visibility 0s linear 0s;
      }

      .fsc-ui .fsc-plan-option {
        appearance: none;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        width: 100%;
        min-height: 40px;
        margin: 0;
        padding: 9px 10px 9px 12px;
        border: 0;
        border-radius: 8px;
        background: transparent;
        color: #000f20;
        font: inherit;
        font-size: var(--fsc-detail-font-size, 13px);
        line-height: 1.25;
        text-align: left;
        cursor: pointer;
        transition: background-color 140ms ease, transform 140ms ease;
      }

      .fsc-ui .fsc-plan-option:hover,
      .fsc-ui .fsc-plan-option:focus-visible {
        outline: none;
        background: #f7f4e1;
      }

      .fsc-ui .fsc-plan-option:active {
        transform: scale(0.995);
      }

      .fsc-ui .fsc-plan-option[aria-selected="true"] {
        background: #f7f4e1;
        font-weight: 600;
      }

      .fsc-ui .fsc-plan-option.is-recommended:not([aria-selected="true"]) {
        background: rgba(207, 205, 112, 0.16);
      }

      .fsc-ui .fsc-plan-option-label-group {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        min-width: 0;
      }

      .fsc-ui .fsc-plan-option-label {
        min-width: 0;
      }

      .fsc-ui .fsc-plan-recommended {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        flex: 0 0 auto;
        padding: 4px 7px;
        border-radius: 999px;
        background: #7c2946;
        color: #ffffff;
        font-size: 10px;
        font-weight: 700;
        line-height: 1;
        letter-spacing: 0.01em;
        white-space: nowrap;
      }

      .fsc-ui .fsc-plan-recommended-star {
        font-size: 10px;
        line-height: 1;
      }

      .fsc-ui .fsc-plan-option-check {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        flex: 0 0 24px;
        border-radius: 999px;
        background: #7c2946;
        color: #ffffff;
        font-size: 13px;
        font-weight: 700;
        line-height: 1;
        opacity: 0;
        transform: scale(0.8);
        transition: opacity 140ms ease, transform 140ms ease;
      }

      .fsc-ui .fsc-plan-option[aria-selected="true"] .fsc-plan-option-check {
        opacity: 1;
        transform: scale(1);
      }

      .fsc-ui .fsc-plan-select--native {
        display: none !important;
      }

      @media (max-width: 749px) {
        .fsc-ui .fsc-plan-trigger {
          min-height: 42px;
          padding-left: 13px;
        }

        .fsc-ui .fsc-plan-trigger-icon {
          width: 28px;
          height: 28px;
          flex-basis: 28px;
        }

        .fsc-ui .fsc-plan-option {
          min-height: 39px;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .fsc-ui .fsc-plan-trigger,
        .fsc-ui .fsc-plan-trigger-icon,
        .fsc-ui .fsc-plan-menu,
        .fsc-ui .fsc-plan-option,
        .fsc-ui .fsc-plan-option-check {
          transition: none !important;
        }
      }
    `;
    document.head.appendChild(style);
  };

  A.nativeBlock = () => {
    if (!S.root) return null;

    const native = Array.from(document.querySelectorAll(A.APPSTLE_SELECTOR)).find(
      (el) => S.root.contains(el)
    );

    if (!native) return null;

    return (
      native.closest('[id^="shopify-block-"]') ||
      native.closest('.shopify-block') ||
      native
    );
  };

  A.removeNativeWidget = () => {
    const block = A.nativeBlock();
    if (!block) return;
    block.setAttribute('data-femme-appstle-native', 'true');
    block.classList.add('femme-appstle-native-block');
  };

  A.recommendedExtrasBlock = () => {
    if (!S.root) return null;

    const productId = String(
      S.product?.id || S.controller?.dataset?.fscProductId || ''
    );

    return (
      Array.from(
        document.querySelectorAll(
          '[data-custom-product-upsell][data-has-upsells="true"]'
        )
      ).find((el) => {
        if (!S.root.contains(el)) return false;
        return (
          !productId || String(el.dataset.currentProductId || '') === productId
        );
      }) || null
    );
  };

  A.positionUi = () => {
    if (!S.ui || !S.root) return;

    A.removeNativeWidget();

    const addButton =
      S.root.querySelector(
        'button.product-form__submit[name="add"], button.femme-add-to-cart-button[name="add"]'
      ) || null;

    if (addButton?.parentNode) {
      if (S.ui.nextElementSibling !== addButton) {
        addButton.parentNode.insertBefore(S.ui, addButton);
      }
      return;
    }

    const form =
      S.root.querySelector('form[data-type="add-to-cart-form"]') ||
      S.root.querySelector('form.main-product-form');
    const buttonGroup = form?.querySelector('.product-form__buttons');
    if (buttonGroup) {
      if (S.ui.parentNode !== buttonGroup) {
        buttonGroup.appendChild(S.ui);
      }
      return;
    }

    const mount =
      form?.closest('product-form') ||
      form?.parentElement ||
      S.root.lastElementChild;

    if (mount && !mount.contains(S.ui)) {
      mount.parentNode?.insertBefore(S.ui, mount);
    }
  };

  A.closePlanMenu = (returnFocus = false) => {
    const refs = S.ui?._f;
    if (!refs?.picker || !refs?.trigger || !refs?.menu) return;

    refs.picker.classList.remove('is-open');
    refs.menu.setAttribute('aria-hidden', 'true');
    refs.trigger.setAttribute('aria-expanded', 'false');
    refs.menu.querySelectorAll('[role="option"]').forEach((option) => {
      option.tabIndex = -1;
    });

    if (returnFocus) refs.trigger.focus();
  };

  A.openPlanMenu = (focusSelected = false) => {
    const refs = S.ui?._f;
    if (!refs?.picker || !refs?.trigger || !refs?.menu || refs.picker.hidden) return;

    refs.picker.classList.add('is-open');
    refs.menu.setAttribute('aria-hidden', 'false');
    refs.trigger.setAttribute('aria-expanded', 'true');

    const selected = refs.menu.querySelector('[role="option"][aria-selected="true"]');
    const first = refs.menu.querySelector('[role="option"]');
    const focusTarget = selected || first;

    refs.menu.querySelectorAll('[role="option"]').forEach((option) => {
      option.tabIndex = option === focusTarget ? 0 : -1;
    });

    if (focusSelected) focusTarget?.focus();
  };

  A.choosePlan = (planId, focusTrigger = true) => {
    const id = String(planId || '');
    const plans = A.getPlans();
    if (!plans.some((candidate) => candidate.id === id)) return false;

    S.selectedPlanId = id;
    S.selectionType = 'subscription';
    A.closePlanMenu(false);
    A.render();

    if (focusTrigger) S.ui?._f?.trigger?.focus();
    return true;
  };

  A.buildUi = () => {
    if (S.ui) return;

    A.installPlanPickerStyles();

    const ui = document.createElement('div');
    ui.className = 'fsc-ui';
    ui.dataset.fscOwner = String(
      S.controller.dataset.fscSectionId || 'appstle'
    );
    ui.dataset.femmeAppstleUi = 'true';
    if (!S.config?.animate) ui.classList.add('fsc-no-animation');

    const offer = S.controller.querySelector('[data-fsc-offer-template]');
    if (offer instanceof HTMLTemplateElement) {
      ui.append(offer.content.cloneNode(true));
    }

    const options = document.createElement('div');
    options.className = 'fsc-options';
    options.setAttribute('role', 'radiogroup');
    options.setAttribute('aria-label', 'Purchase options');

    const subCard = document.createElement('div');
    subCard.className = 'fsc-option-card fsc-option-card--subscription';

    const onceCard = document.createElement('div');
    onceCard.className = 'fsc-option-card fsc-option-card--one-time';

    const sub = head('subscription');
    const once = head('one_time');

    const details = document.createElement('div');
    details.className = 'fsc-details';

    const inner = document.createElement('div');
    inner.className = 'fsc-details-inner';

    const content = document.createElement('div');
    content.className = 'fsc-details-content';

    const benefits = document.createElement('ul');
    benefits.className = 'fsc-benefits';

    const wrap = document.createElement('div');
    wrap.className = 'fsc-select-wrap';

    const select = document.createElement('select');
    select.className = 'fsc-plan-select fsc-plan-select--native';
    select.setAttribute('aria-label', 'Delivery frequency');
    select.tabIndex = -1;
    select.hidden = true;

    const picker = document.createElement('div');
    picker.className = 'fsc-plan-picker';

    const trigger = document.createElement('div');
    trigger.className = 'fsc-plan-trigger';
    trigger.setAttribute('role', 'button');
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.tabIndex = 0;

    const triggerLabel = document.createElement('span');
    triggerLabel.className = 'fsc-plan-trigger-label';

    const triggerIcon = document.createElement('span');
    triggerIcon.className = 'fsc-plan-trigger-icon';
    triggerIcon.setAttribute('aria-hidden', 'true');

    trigger.append(triggerLabel, triggerIcon);

    const menu = document.createElement('div');
    menu.className = 'fsc-plan-menu';
    menu.setAttribute('role', 'listbox');
    menu.setAttribute('aria-label', 'Delivery frequency');
    menu.setAttribute('aria-hidden', 'true');

    picker.append(trigger, menu);

    const plan = document.createElement('div');
    plan.className = 'fsc-plan-static';
    plan.hidden = true;

    wrap.append(select, picker, plan);
    content.append(benefits, wrap);
    inner.append(content);
    details.append(inner);
    subCard.append(sub.button, details);
    onceCard.append(once.button);

    if (S.config?.subscriptionFirst !== false) {
      options.append(subCard, onceCard);
    } else {
      options.append(onceCard, subCard);
    }

    ui.append(options);
    ui._f = {
      subCard,
      onceCard,
      sub,
      once,
      benefits,
      wrap,
      select,
      picker,
      trigger,
      triggerLabel,
      menu,
      plan
    };

    sub.button.addEventListener('click', () => {
      S.selectionType = 'subscription';
      A.render();
    });

    once.button.addEventListener('click', () => {
      S.selectionType = 'one_time';
      A.closePlanMenu(false);
      A.render();
    });

    select.addEventListener('change', () => {
      A.choosePlan(select.value, false);
    });

    trigger.addEventListener('click', () => {
      if (picker.classList.contains('is-open')) {
        A.closePlanMenu(false);
      } else {
        A.openPlanMenu(false);
      }
    });

    trigger.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        trigger.click();
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        A.openPlanMenu(true);
      } else if (event.key === 'Escape') {
        A.closePlanMenu(false);
      }
    });

    menu.addEventListener('click', (event) => {
      const option = event.target.closest('[data-fsc-plan-option]');
      if (!option) return;
      A.choosePlan(option.dataset.planId, true);
    });

    menu.addEventListener('keydown', (event) => {
      const optionsList = Array.from(menu.querySelectorAll('[role="option"]'));
      if (!optionsList.length) return;

      const focused = document.activeElement?.closest?.('[role="option"]');
      const currentIndex = Math.max(0, optionsList.indexOf(focused));
      let nextIndex = currentIndex;

      if (event.key === 'Enter' || event.key === ' ') {
        if (focused?.dataset?.planId) {
          event.preventDefault();
          A.choosePlan(focused.dataset.planId, true);
        }
        return;
      }

      if (event.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % optionsList.length;
      } else if (event.key === 'ArrowUp') {
        nextIndex = (currentIndex - 1 + optionsList.length) % optionsList.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = optionsList.length - 1;
      } else if (event.key === 'Escape') {
        event.preventDefault();
        A.closePlanMenu(true);
        return;
      } else {
        return;
      }

      event.preventDefault();
      optionsList.forEach((option, index) => {
        option.tabIndex = index === nextIndex ? 0 : -1;
      });
      optionsList[nextIndex]?.focus();
    });

    document.addEventListener('click', (event) => {
      if (!picker.classList.contains('is-open')) return;
      if (picker.contains(event.target)) return;
      A.closePlanMenu(false);
    });

    S.ui = ui;
    A.positionUi();
  };

  A.renderPlanSelect = (plans, currentPlan) => {
    const refs = S.ui?._f;
    if (!refs?.select || !refs?.wrap || !refs?.picker || !refs?.menu) return;

    refs.select.replaceChildren();
    refs.menu.replaceChildren();

    const recommendedPlanId = A.recommendedPlanId(plans);

    plans.forEach((candidate) => {
      const displayLabel = A.planDisplayLabel(candidate);
      const isRecommended =
        Boolean(recommendedPlanId) &&
        String(candidate.id) === String(recommendedPlanId);

      const option = document.createElement('option');
      option.value = candidate.id;
      option.textContent = displayLabel;
      option.selected = candidate.id === currentPlan?.id;
      refs.select.append(option);

      const choice = document.createElement('div');
      choice.className = 'fsc-plan-option';
      if (isRecommended) choice.classList.add('is-recommended');
      choice.dataset.fscPlanOption = 'true';
      choice.dataset.planId = String(candidate.id);
      if (isRecommended) choice.dataset.fscRecommended = 'true';
      choice.setAttribute('role', 'option');
      choice.setAttribute(
        'aria-selected',
        candidate.id === currentPlan?.id ? 'true' : 'false'
      );
      choice.tabIndex = -1;

      const labelGroup = document.createElement('span');
      labelGroup.className = 'fsc-plan-option-label-group';

      const label = document.createElement('span');
      label.className = 'fsc-plan-option-label';
      label.textContent = displayLabel;
      labelGroup.append(label);

      if (isRecommended) {
        const badge = document.createElement('span');
        badge.className = 'fsc-plan-recommended';

        const star = document.createElement('span');
        star.className = 'fsc-plan-recommended-star';
        star.setAttribute('aria-hidden', 'true');
        star.textContent = '★';

        const badgeText = document.createElement('span');
        badgeText.textContent = 'Recommended';

        badge.append(star, badgeText);
        labelGroup.append(badge);
      }

      const check = document.createElement('span');
      check.className = 'fsc-plan-option-check';
      check.setAttribute('aria-hidden', 'true');
      check.textContent = '✓';

      choice.append(labelGroup, check);
      refs.menu.append(choice);
    });

    if (plans.length > 1) {
      refs.wrap.classList.remove('is-static');
      refs.select.hidden = true;
      refs.plan.hidden = true;
      refs.picker.hidden = false;
      refs.select.value = String(currentPlan?.id || '');
      refs.triggerLabel.textContent = A.planDisplayLabel(currentPlan);

      const menuOpen = refs.picker.classList.contains('is-open');
      refs.menu.setAttribute('aria-hidden', menuOpen ? 'false' : 'true');
      refs.menu.querySelectorAll('[role="option"]').forEach((option) => {
        const selected =
          String(option.dataset.planId) === String(currentPlan?.id);
        option.setAttribute('aria-selected', selected ? 'true' : 'false');
        option.tabIndex = menuOpen && selected ? 0 : -1;
      });
    } else {
      A.closePlanMenu(false);
      refs.wrap.classList.add('is-static');
      refs.select.hidden = true;
      refs.picker.hidden = true;
      refs.plan.hidden = false;
      refs.plan.textContent = A.planDisplayLabel(currentPlan);
    }
  };

  A.renderBenefits = (plan, variant) => {
    const list = S.ui?._f?.benefits;
    if (!list) return;

    const percent = A.saving(plan, variant);
    const values = [];
    const delivery = A.deliveryBenefit(plan);

    if (
      S.config?.showSavingBenefit &&
      String(S.config?.savingBenefit || '').trim() &&
      percent > 0
    ) {
      values.push(A.token(S.config.savingBenefit, percent));
    }

    (S.config?.benefits || []).forEach((rawValue) => {
      let value = A.token(rawValue, percent);
      value = value.replace(/\[delivery\]/gi, delivery);
      if (/^delivers monthly$/i.test(value)) value = delivery;
      if (/^20% off after 3 consecutive months$/i.test(value)) {
        value = '20% off from your 4th order';
      }
      if (value) values.push(value);
    });

    list.innerHTML = '';
    list.hidden = !values.length;

    values.forEach((value) => {
      const li = document.createElement('li');
      li.className = 'fsc-benefit';
      const span = document.createElement('span');
      span.textContent = value;
      li.append(span);
      list.append(li);
    });
  };

  A.activePlanId = () => {
    if (S.selectionType !== 'subscription') return '';
    const activePlan = A.getPlan?.();
    const id = String(activePlan?.id || S.selectedPlanId || '');
    if (id) S.selectedPlanId = id;
    return id;
  };

  A.applyPurchaseStateToItem = (item) => {
    if (!item || typeof item !== 'object') return item;

    const planId = A.activePlanId();
    if (planId) {
      item.selling_plan = Number(planId) || planId;
    } else {
      delete item.selling_plan;
      delete item.sellingPlan;
      delete item.sellingPlanId;
    }

    return item;
  };

  A.patchCartBuilders = () => {
    [window.CustomProductUpsell, window.FemmeEasyDraw].forEach((api) => {
      if (!api || typeof api.buildMainItem !== 'function') return;

      const current = api.buildMainItem;
      if (current.__femmeAppstlePatched) return;

      const wrapped = function (...args) {
        const result = current.apply(this, args);

        if (result && typeof result.then === 'function') {
          return result.then((item) => A.applyPurchaseStateToItem(item));
        }

        return A.applyPurchaseStateToItem(result);
      };

      wrapped.__femmeAppstlePatched = true;
      wrapped.__femmeAppstleOriginal = current;
      api.buildMainItem = wrapped;
    });
  };

  A.formData = (event) => {
    if (!event.formData) return;

    const planId = A.activePlanId();
    if (planId) {
      event.formData.set('selling_plan', planId);
    } else {
      event.formData.delete('selling_plan');
    }
  };

  A.syncForms = () => {
    const planId = A.activePlanId();

    A.patchCartBuilders();
    A.removeNativeWidget();

    Array.from(S.root?.querySelectorAll('form[action*="/cart/add"]') || []).forEach(
      (form) => {
        if (!S.boundForms.has(form)) {
          form.addEventListener('formdata', A.formData);
          S.boundForms.add(form);
        }

        form.dataset.femmeAppstleSelection = S.selectionType;
        form.dataset.femmeAppstlePlan = planId;

        /* Keep Appstle-owned selling_plan inputs in the DOM. The formdata
           listener and cart payload guards below set the authoritative value
           at submission time, so the native widget can retain its own state
           without competing with the femme UI. */

        let managed = form.querySelector(
          'input[data-femme-appstle-plan="true"]'
        );

        if (!planId) {
          managed?.remove();
          return;
        }

        if (!managed) {
          managed = document.createElement('input');
          managed.type = 'hidden';
          managed.name = 'selling_plan';
          managed.dataset.femmeAppstlePlan = 'true';
          form.append(managed);
        }

        managed.disabled = false;
        managed.value = planId;
        managed.setAttribute('value', planId);
      }
    );
  };

  A.bindPreAddSync = () => {
    if (A._preAddSyncBound) return;
    A._preAddSyncBound = true;

    const syncBeforeAdd = (event) => {
      const target = event.target;
      if (!(target instanceof Element) || !S.root) return;

      const control = target.closest(
        'button.product-form__submit[name="add"], button[name="add"], input[name="add"], form[action*="/cart/add"] [type="submit"]'
      );

      if (!control || !S.root.contains(control)) return;
      A.syncForms();
    };

    window.addEventListener('pointerdown', syncBeforeAdd, true);
    window.addEventListener('click', syncBeforeAdd, true);
  };
})();
